import { useEffect, useState, FormEvent, useContext } from "react";
import { getTokenForWeb, getCustomerDetailsByToken, driverLoginOps } from "../services/auth";
import { checkDriverPanelAccess, IDriverPanelAccess } from "../services/checkRole";
import { decrypt, encrypt, getOrCreateDeviceId, validUserLogin } from "../utils/Util";
import { IUserLogin } from "../models/IUserLogin";
import { ILoginInfo } from "../models/ILoginInfo";
import { Context } from "../utils/AppContext";
import { useNavigate } from "react-router-dom";
import LoginRoleSwitch from "../components/LoginRoleSwitch";

function DriverLogin() {
  const navigate = useNavigate();
  const context = useContext(Context);

  const hasSaved = !!(localStorage.drvUsername && localStorage.drvPassword);
  const [remember, setRemember] = useState(hasSaved);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const [userdata, setUserdata] = useState<IUserLogin>({
    email: localStorage.drvUsername ? decrypt(localStorage.drvUsername) : "",
    password: localStorage.drvPassword ? decrypt(localStorage.drvPassword) : "",
    deviceId: getOrCreateDeviceId(),
  });

  useEffect(() => {
    // Web token al
    getTokenForWeb()
      .then((res) => {
        if (res?.data) {
          localStorage.setItem("MA", encrypt(JSON.stringify(res.data)));
        }
      })
      .catch((err) => {
        console.log("error", err.message);
      });
  }, []);

  const handleLogin = (evt: FormEvent) => {
    evt.preventDefault();
    const status = validUserLogin(userdata.email, userdata.password);
    if (!status) return;

    if (remember) {
      localStorage.setItem("drvUsername", encrypt(userdata.email));
      localStorage.setItem("drvPassword", encrypt(userdata.password));
    } else {
      localStorage.removeItem("drvUsername");
      localStorage.removeItem("drvPassword");
    }

    setErrorMessage("");
    setLoading(true);
    driverLoginOps(userdata)
      .then(async (res) => {
        const tokenData = res.data;
        // Rol kontrolü için önce token'ı geçici olarak sakla (yetkili istekler için gerekli).
        const provisional: ILoginInfo = { ...tokenData, restourantId: "" };
        localStorage.setItem("user", encrypt(JSON.stringify(provisional)));

        let access: IDriverPanelAccess;
        try {
          access = await checkDriverPanelAccess();
        } catch {
          localStorage.removeItem("user");
          setErrorMessage("Yetki bilgileri alınamadı. Lütfen tekrar deneyin.");
          setLoading(false);
          return;
        }

        if (!access.isSuperAdmin && !access.isRestaurantAdmin && !access.isDriver) {
          localStorage.removeItem("user");
          setErrorMessage("Bu hesabın kurye paneline erişim yetkisi yok.");
          setLoading(false);
          return;
        }

        // Sadece saf RestaurantDriver hesapları kurye paneline kilitlenir;
        // SuperAdmin/RestourantAdmin bu ekrandan girse bile diğer sayfalara erişebilir.
        const isDriverOnly = access.isDriver && !access.isSuperAdmin && !access.isRestaurantAdmin;
        const finalInfo: ILoginInfo = { ...tokenData, restourantId: "", isDriverOnly };
        localStorage.setItem("user", encrypt(JSON.stringify(finalInfo)));

        try {
          const detailsRes = await getCustomerDetailsByToken();
          const merged = { ...finalInfo, ...detailsRes.data, isDriverOnly };
          localStorage.setItem("user", encrypt(JSON.stringify(merged)));
          context.setName(merged as any);
        } catch {
          context.setName(finalInfo as any);
        }

        navigate("/kuryePaneli", { replace: true });
      })
      .catch((err) => {
        localStorage.removeItem("user");
        const data = err?.response?.data;
        const apiMsg = Array.isArray(data) && data[0]?.description
          ? data[0].description
          : err.message;
        setErrorMessage(apiMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fff7ed 0%, #fafafa 60%, #fef3c7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
          padding: '44px 40px 36px',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo / Başlık */}
        <div className="text-center mb-4">
          <div
            style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
              borderRadius: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              boxShadow: '0 4px 14px rgba(234,88,12,0.35)',
            }}
          >
            <i className="bi bi-bicycle text-white" style={{ fontSize: '1.6rem' }}></i>
          </div>
          <h4 className="fw-bold mb-1" style={{ color: '#111827', letterSpacing: '-0.3px' }}>
            Kurye Girişi
          </h4>
          <p className="text-muted small mb-0">Kurye panelinize giriş yapın</p>
        </div>

        {/* Restoran / Kurye seçici */}
        <LoginRoleSwitch active="kurye" />

        {/* Hata mesajı */}
        {errorMessage && (
          <div
            className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3"
            style={{ background: '#fef2f2', border: '1.5px solid #fecaca', color: '#b91c1c', fontSize: '0.85rem' }}
          >
            <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage("")}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', padding: 0 }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin}>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              E-posta
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-envelope-fill"></i>
              </span>
              <input
                type="email"
                value={userdata.email}
                onChange={(e) => setUserdata({ ...userdata, email: e.target.value })}
                className="form-control"
                placeholder="ornek@email.com"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
            </div>
          </div>

          {/* Şifre */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              Şifre
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                type={showPass ? "text" : "password"}
                value={userdata.password}
                onChange={(e) => setUserdata({ ...userdata, password: e.target.value })}
                className="form-control"
                placeholder="••••••••"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRight: 'none',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
              <button
                type="button"
                className="input-group-text"
                onClick={() => setShowPass(!showPass)}
                style={{
                  background: '#f9fafb',
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  color: '#9ca3af',
                  cursor: 'pointer',
                }}
              >
                <i className={`bi ${showPass ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
              </button>
            </div>
          </div>

          {/* Beni Hatırla */}
          <div className="d-flex align-items-center mb-4">
            <div
              onClick={() => setRemember(!remember)}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '5px',
                border: `2px solid ${remember ? '#ea580c' : '#d1d5db'}`,
                background: remember ? '#ea580c' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
            >
              {remember && <i className="bi bi-check text-white" style={{ fontSize: '0.7rem', lineHeight: 1 }}></i>}
            </div>
            <label
              onClick={() => setRemember(!remember)}
              className="ms-2 small"
              style={{ color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}
            >
              Beni hatırla
            </label>
          </div>

          {/* Giriş Butonu */}
          <button
            type="submit"
            className="btn w-100"
            disabled={loading}
            style={{
              background: loading ? '#fdba74' : 'linear-gradient(135deg, #f59e0b, #ea580c)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.97rem',
              borderRadius: '10px',
              padding: '11px',
              border: 'none',
              boxShadow: '0 4px 14px rgba(234,88,12,0.35)',
              transition: 'all 0.2s ease',
              letterSpacing: '0.2px',
            }}
          >
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Giriş yapılıyor...</>
            ) : (
              <><i className="bi bi-box-arrow-in-right me-2"></i>Giriş Yap</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default DriverLogin;
